import { useMemo, useState, useCallback } from "react";
import { SortDirection, SortState } from "../components/FCObjets/FCSortableHeader";

type Accessor<T> = (item: T) => string | number | Date | null | undefined;

export interface UseTableSortResult<T, K extends string> {
    sortedData: T[];
    sortState: SortState<K>;
    handleSort: (key: K) => void;
}

/**
 * Tri générique de tableau cyclant asc → desc → neutre à chaque clic.
 *
 * @param data tableau source
 * @param accessors map clé → fonction d'accès à la valeur triable
 * @param defaultSort état initial optionnel
 */
export function useTableSort<T, K extends string>(
    data: T[],
    accessors: Record<K, Accessor<T>>,
    defaultSort: SortState<K> = { key: null, direction: null },
): UseTableSortResult<T, K> {
    const [sortState, setSortState] = useState<SortState<K>>(defaultSort);

    const handleSort = useCallback((key: K) => {
        setSortState((prev) => {
            if (prev.key !== key) return { key, direction: "asc" };
            const nextDirection: SortDirection =
                prev.direction === "asc" ? "desc" :
                    prev.direction === "desc" ? null : "asc";
            return { key: nextDirection === null ? null : key, direction: nextDirection };
        });
    }, []);

    const sortedData = useMemo(() => {
        if (!sortState.key || !sortState.direction) return data;

        const accessor = accessors[sortState.key];
        if (!accessor) return data;

        const dir = sortState.direction === "asc" ? 1 : -1;
        const arr = [...data];
        arr.sort((a, b) => {
            const va = accessor(a);
            const vb = accessor(b);

            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;

            if (va instanceof Date && vb instanceof Date) {
                return (va.getTime() - vb.getTime()) * dir;
            }
            if (typeof va === "number" && typeof vb === "number") {
                return (va - vb) * dir;
            }
            return String(va).localeCompare(String(vb), "fr", { sensitivity: "base" }) * dir;
        });
        return arr;
    }, [data, sortState, accessors]);

    return { sortedData, sortState, handleSort };
}
